import {useEffect} from 'react';

import {useLogicTestDataCollectionResult} from '@/api/logicTestIrt/useLogicTestDataCollectionResult';
import {useLogicTestStatus} from '@/api/logicTestIrt/useLogicTestStatus';
import {useLoggedInUser} from '@/api/users/useLoggedInUser';
import {ErrorState} from '@/componentLibrary/blocks/ErrorState';
import {LogicTestReport} from '@/componentLibrary/blocks/testReports/LogicTest/LogicTestReport';
import {PrimaryButton} from '@/componentLibrary/components/buttons/PrimaryButton';
import {ModalEmpty} from '@/componentLibrary/components/modals/ModalEmpty';
import {Spinner} from '@/componentLibrary/components/spinners';
import {UserDemographics} from '@/pages/UserDemographics';

import {LogicalAbilityEstimate} from '@/api/types/__generated__/graphql';
import {
    CompletedWrapper,
    ModalContent,
    WaitingForResult,
    Wrapper
} from '@/pages/LogicTestDataCollection/pages/CompletedDataCollectionTest/styled';
import {CompletedDataCollectionTestProps} from '@/pages/LogicTestDataCollection/pages/CompletedDataCollectionTest/types';
import {ProlificCompletedContent} from './components/ProlificCompletedContent/ProlificCompletedContent.js';
import {useCompletedDataCollectionTestLogic} from './logic';

export function CompletedDataCollectionTest({logicTestId}: CompletedDataCollectionTestProps) {
    const {
        logicTestStatus: logicTest,
        loading: loadingTest,
        error: testError,
        refetch: refetchTest
    } = useLogicTestStatus(logicTestId);

    const {
        loading: loadingResult,
        error: resultError,
        refetch: refetchResult,
        logicTestDataCollectionResult,
        stopPolling
    } = useLogicTestDataCollectionResult(logicTestId);

    const {
        loading: loadingUser,
        error: userError,
        refetch: refetchUser,
        user
    } = useLoggedInUser();

    const {
        reportModalIsOpen,
        openReportModal,
        closeReportModal,
        demographicsModalIsOpen,
        closeDemographicsModal
    } = useCompletedDataCollectionTestLogic();

    useEffect(() => {
        if (logicTestDataCollectionResult) {
            stopPolling();
        }
    }, [logicTestDataCollectionResult, stopPolling]);

    if (loadingTest || loadingUser) {
        return (
            <Wrapper>
                <Spinner />
            </Wrapper>
        );
    }

    if (testError) {
        return <ErrorState error={testError} refetch={refetchTest} />;
    }

    if (resultError) {
        return <ErrorState error={resultError} refetch={refetchResult} />;
    }

    if (userError) {
        return <ErrorState error={userError} refetch={refetchUser} />;
    }

    if (!user) {
        return null;
    }

    if (!logicTest) {
        return <Wrapper>Test was not found.</Wrapper>;
    }

    if (!user.hasSubmittedDemographicsForm && demographicsModalIsOpen) {
        return (
            <ModalEmpty noCenterWrapper showCloseIcon={false}>
                <ModalContent>
                    <UserDemographics isMandatory onSubmitSuccess={closeDemographicsModal} />
                </ModalContent>
            </ModalEmpty>
        );
    }
    return (
        <Wrapper>
            {!loadingResult && logicTestDataCollectionResult ? (
                <CompletedWrapper>
                    <ProlificCompletedContent
                        completionCode={logicTestDataCollectionResult.completionCode}
                    />
                    <PrimaryButton onClick={openReportModal}>See result</PrimaryButton>
                </CompletedWrapper>
            ) : (
                <WaitingForResult>Waiting for result...</WaitingForResult>
            )}

            {reportModalIsOpen ? (
                <LogicTestReport
                    close={closeReportModal}
                    logicalAbilityEstimate={
                        logicTestDataCollectionResult as LogicalAbilityEstimate
                    }
                />
            ) : null}
        </Wrapper>
    );
}
